/**
 * Casamento no tempo entre os quadros do loop e as horas do GSMaP, e o
 * frescor do índice — funções puras (testadas em `gsmap.test.ts`).
 *
 * Cada arquivo da JAXA é a chuva MÉDIA de uma hora (13:30–14:30, por
 * exemplo), refeita a cada 30 min. A imagem do satélite das 14:10 mostra a
 * hora cujo meio está mais perto dela (13:30–14:30, meio às 14:00).
 *
 * Até 45 min do meio, para trás: a hora contém a imagem ou terminou no
 * máximo 15 min antes dela; para a frente, só 30 (a hora já tinha começado). Esses 15 min cobrem a espera de cada meia hora — a JAXA publica
 * a hora 16:30–17:30 às 17:31, e até lá a imagem das 17:10 ficaria sem chuva
 * (medido em 23/10/2026; o app abre justamente na imagem mais nova). O painel
 * e o clique dizem a hora exata. Mais longe que isso, o quadro fica sem chuva,
 * nunca com a de horas atrás.
 */
import { MINUTE_MS } from '../lib/time';
import type { QuadroGsmap } from './api';

/** Meio da hora ANTES da imagem: até 45 min (a hora terminou até 15 min antes dela). */
export const TOLERANCIA_MS = 45 * MINUTE_MS;
/** Meio da hora DEPOIS da imagem: até 30 min (a hora já tinha começado nela). */
export const TOLERANCIA_FRENTE_MS = 30 * MINUTE_MS;

/**
 * O quadro do GSMaP a exibir no instante `t`, ou null: o meio mais perto entre
 * o de antes (até 45 min) e o de depois (até 30 min) — uma hora que começa
 * depois da imagem nunca vale para ela. Empate: a de antes. Serve a qualquer
 * lista de horas do GSMaP (o rastreio da chuva usa a mesma regra).
 */
export function quadroParaInstante<Q = QuadroGsmap>(quadros: readonly Q[], meios: readonly number[], t: number | null): Q | null {
  if (t === null || !quadros.length) return null;
  const antes = ultimoAte(meios, t);
  const depois = antes + 1 < meios.length ? antes + 1 : -1;
  const dAntes = antes >= 0 && t - meios[antes] <= TOLERANCIA_MS ? t - meios[antes] : Infinity;
  const dDepois = depois >= 0 && meios[depois] - t <= TOLERANCIA_FRENTE_MS ? meios[depois] - t : Infinity;
  if (dAntes === Infinity && dDepois === Infinity) return null;
  return dAntes <= dDepois ? quadros[antes] : quadros[depois];
}

/** Último índice com `meios[i] ≤ t` (crescente), ou −1. */
function ultimoAte(meios: readonly number[], t: number): number {
  let lo = 0;
  let hi = meios.length - 1;
  let r = -1;
  while (lo <= hi) {
    const m = (lo + hi) >> 1;
    if (meios[m] <= t) {
      r = m;
      lo = m + 1;
    } else hi = m - 1;
  }
  return r;
}

/**
 * A JAXA publica a hora logo que ela termina e a máquina olha a cada 5 min:
 * o normal é a hora mais nova ter terminado há 5–15 min. Passando de 75 min
 * (duas publicações perdidas), avisa.
 */
export const ATRASO_AVISO_MIN = 75;
/** Seis horas: o que está no mapa é história. */
export const ATRASO_GRAVE_MIN = 6 * 60;

function quanto(min: number): string {
  return min >= 120 ? `${Math.floor(min / 60)} h` : `${Math.round(min)} min`;
}

/** Aviso sobre a hora mais nova publicada (pelo FIM dela), ou null quando está em dia. */
export function avisoDeFrescor(fimDoUltimo: number | null, agora: number = Date.now()): string | null {
  if (fimDoUltimo === null) return null;
  const min = Math.max(0, (agora - fimDoUltimo) / MINUTE_MS);
  if (min >= ATRASO_GRAVE_MIN) return `A chuva por satélite está sem hora nova há ${quanto(min)}. O que está no mapa não é a chuva de agora.`;
  if (min >= ATRASO_AVISO_MIN) return `Chuva por satélite sem hora nova há ${quanto(min)} — o normal é 5–15 min depois do fim da hora.`;
  return null;
}
